// https://dev.to/ms_yogii/useaxios-a-simple-custom-hook-for-calling-apis-using-axios-2dkj
import { useRef, useCallback, useEffect, useState } from 'react'
import { AxiosError, AxiosPromise } from 'axios'
import useIsMounted from './useIsMounted'

const useAxiosResponse = <T = any,>(
  fetchCallback: () => AxiosPromise<T>,
  autoFetch = true
): [
  {
    response: T | undefined
    error: AxiosError | undefined
    loading: boolean
    status: number
  },
  () => void
] => {
  const [status, setStatus] = useState<number>(0)
  const [response, setResponse] = useState<T | undefined>(undefined)
  const [error, setError] = useState<AxiosError | undefined>(undefined)
  const [loading, setLoading] = useState<boolean>(autoFetch)
  const isMounted = useIsMounted()
  const fetchCount = useRef(0)

  const fetchData = useCallback(() => {
    if (isMounted()) {
      fetchCount.current++
      const current = fetchCount.current
      setLoading(true)
      fetchCallback()
        .then((res) => {
          if (isMounted() && current === fetchCount.current) {
            setStatus(res.status)
            setResponse(res.data)
          }
        })
        .catch((err) => {
          if (isMounted() && current === fetchCount.current) {
            setStatus(err.response.status)
            setError(err)
            setResponse(err.response.data)
          }
        })
        .finally(() => {
          if (isMounted()) {
            setLoading(false)
          }
        })
    }
  }, [fetchCallback, isMounted])

  useEffect(() => {
    if (autoFetch) fetchData()
  }, [fetchCallback, autoFetch, fetchData])

  return [{ response, error, loading, status }, fetchData]
}

export default useAxiosResponse
